import React from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  CalendarMonth as CalendarIcon,
  PhotoLibrary as PhotoIcon,
  Router as RouterIcon,
  Videocam as VideocamIcon,
  VpnKey as KeyIcon,
  RocketLaunch as DeployIcon,
  History as HistoryIcon,
  Inventory2 as InventoryIcon,
  Description as DescriptionIcon,
  FactCheck as AgentIcon,
  Map as WallpadMapIcon,
  Checklist as QaIcon,
} from '@mui/icons-material';
import usePermissions from '../../hooks/usePermissions';
import { hasRoleAtLeast, UserRole } from '../../utils/permissions';

/**
 * 현장 페이지 사이드바의 섹션 식별자.
 * URL 라우팅은 하지 않으며, ProjectDashboardPage 내부 state 로만 사용.
 *
 * sidebar-merge-schedules: 일정/출장 일지/장애 이력 메뉴를 단일 'schedules' 로 통합.
 * 진입 후 탭으로 카테고리 전환. 'journal' / 'incidents' 는 외부 navigate 대상
 * 으로만 유지되며 사이드바엔 노출하지 않는다.
 */
export type DashboardSection =
  | 'overview'
  | 'agent'
  | 'deploy'
  | 'schedules'
  | 'journal'
  | 'incidents'
  | 'photos'
  | 'materials'
  | 'documents'
  | 'qa'
  | 'network'
  | 'nvr'
  | 'accounts'
  | 'wallpad'
  | 'history';

interface DashboardSidebarProps {
  active: DashboardSection;
  onChange: (section: DashboardSection) => void;
  /** 각 섹션의 빠른 카운트(있을 때만 chip 으로 표시). */
  counts?: Partial<Record<DashboardSection, number | undefined>>;
}

interface MenuItem {
  id: DashboardSection;
  label: string;
  icon: React.ReactNode;
  groupTop?: boolean; // true 면 그 위에 divider
  requireRole?: UserRole;
}

const MENU: MenuItem[] = [
  { id: 'overview', label: '개요', icon: <DashboardIcon fontSize="small" /> },
  { id: 'agent', label: '주차 에이전트', icon: <AgentIcon fontSize="small" /> },
  { id: 'deploy', label: '배포', icon: <DeployIcon fontSize="small" /> },
  // 일정/출장 일지/장애 이력은 단일 '일정' 안에서 탭으로 전환 — sidebar-merge-schedules.
  { id: 'schedules', label: '일정', icon: <CalendarIcon fontSize="small" />, groupTop: true },
  { id: 'photos', label: '사진', icon: <PhotoIcon fontSize="small" /> },
  // 자재는 별도 페이지로 navigate (사이드바 active state 변경 대신).
  { id: 'materials', label: '자재', icon: <InventoryIcon fontSize="small" /> },
  // 파일 보관함도 별도 페이지로 navigate (현장별 임의 형식 파일).
  { id: 'documents', label: '파일 보관함', icon: <DescriptionIcon fontSize="small" /> },
  // 공통 QA 리스트를 가져와 항목별 결과를 기록 — 별도 페이지로 navigate.
  // 만공차등·입구현황판 확인용 주차 테스트도 이 페이지의 [주차 테스트] 탭에 있다.
  { id: 'qa', label: 'QA', icon: <QaIcon fontSize="small" /> },
  { id: 'network', label: 'IP / 네트워크', icon: <RouterIcon fontSize="small" />, groupTop: true },
  // NVR 원격등록 템플릿 — 구역별 문서, NVR 형식 .xls 내보내기.
  { id: 'nvr', label: 'NVR 템플릿', icon: <VideocamIcon fontSize="small" /> },
  { id: 'accounts', label: '계정 정보', icon: <KeyIcon fontSize="small" /> },
  // 월패드(HT) 등록용 지도 이미지 + 좌표 계수 생성 — 둘은 한 세트로 같이 뽑는다.
  { id: 'wallpad', label: '월패드 지도', icon: <WallpadMapIcon fontSize="small" /> },
  // 누가 어떤 글을 언제 등록·수정·삭제했는지 — 외부 페이지로 navigate (이 현장만 필터).
  { id: 'history', label: '히스토리', icon: <HistoryIcon fontSize="small" /> },
];

/** 현재 사용자 권한으로 볼 수 있는 메뉴만. */
const useVisibleMenu = (): MenuItem[] => {
  const { role } = usePermissions();
  return MENU.filter((m) => !m.requireRole || hasRoleAtLeast(role, m.requireRole));
};

/**
 * 좌측 고정 폭 사이드바. 7개 섹션 메뉴 + active 강조 + 옵션 카운트.
 */
const DashboardSidebar: React.FC<DashboardSidebarProps> = ({ active, onChange, counts }) => {
  const menu = useVisibleMenu();
  return (
    <Box
      component="nav"
      aria-label="현장 운영 메뉴"
      sx={{
        width: 220,
        flexShrink: 0,
        borderRight: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
      }}
    >
      <Typography
        variant="overline"
        sx={{ px: 2, pt: 2, pb: 1, display: 'block', color: 'text.secondary', letterSpacing: 1 }}
      >
        현장 운영
      </Typography>
      <List dense disablePadding sx={{ py: 0 }}>
        {menu.map((m) => {
          const count = counts?.[m.id];
          return (
            <React.Fragment key={m.id}>
              {m.groupTop && <Divider sx={{ my: 0.5 }} />}
              <ListItemButton
                selected={active === m.id}
                onClick={() => onChange(m.id)}
                sx={{
                  px: 2,
                  py: 0.75,
                  '&.Mui-selected': {
                    borderLeft: '3px solid',
                    borderColor: 'primary.main',
                    pl: 'calc(16px - 3px)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{ minWidth: 32, color: active === m.id ? 'primary.main' : 'inherit' }}
                >
                  {m.icon}
                </ListItemIcon>
                <ListItemText
                  primary={m.label}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: active === m.id ? 600 : 400,
                  }}
                />
                {typeof count === 'number' && count > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {count}
                  </Typography>
                )}
              </ListItemButton>
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

/**
 * 모바일(md 미만) 전용 섹션 내비게이션 — 사이드바가 숨겨지는 폭에서
 * 동일한 MENU 를 가로 스크롤 탭으로 노출한다. md 이상에서는 미렌더.
 *
 * 'journal' / 'incidents' 는 사이드바와 마찬가지로 메뉴에 없으므로
 * 통합 '일정' 탭을 활성 표시한다.
 */
export const MobileSectionNav: React.FC<DashboardSidebarProps> = ({ active, onChange }) => {
  const menu = useVisibleMenu();
  const tabValue = menu.some((m) => m.id === active) ? active : 'schedules';
  return (
    <Tabs
      value={tabValue}
      onChange={(_, v) => onChange(v as DashboardSection)}
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      aria-label="현장 운영 메뉴"
      sx={{
        display: { xs: 'flex', md: 'none' },
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        minHeight: 44,
      }}
    >
      {menu.map((m) => (
        <Tab
          key={m.id}
          value={m.id}
          icon={m.icon as React.ReactElement}
          iconPosition="start"
          label={m.label}
          sx={{ minHeight: 44, textTransform: 'none', px: 1.5, fontSize: '0.8rem' }}
        />
      ))}
    </Tabs>
  );
};

export default DashboardSidebar;
